import { RefObject, useEffect } from 'react';

import { measure } from '@palette.dev/browser';
import cx from 'classnames';
import Dropdown from 'react-dropdown';

import { updateDropDown } from 'actions/placeorder';
import { updateUserPreference } from 'actions/user';
import { UNIT_OF_ASSETS_QUANTITY } from 'constants/constants';
import { isFutures, isOptions, isSpotContract } from 'helpers/utils';
import { APP_DRAWER_TAB_ID_MAPPING } from 'reducers/initialstate/appDrawer';
import { appDrawerSelector } from 'selectors/otherSelectors';
import {
  calculatedQuantityOnDropdownChangeSelector,
  defaultCurrencySelector,
  quantityCurrencyInPreferencesSelector,
  quantityDropdownValueSelector,
  quantityInputDropdownValue,
  quantityInputValue,
} from 'selectors/placeOrderSelector';
import { selectedProductSelector } from 'selectors/tradeSelectors';
import { useAppDispatch, useAppSelector, useAppSelectorOnEvent } from 'storeHooks';
import { DropDownArrowDownIcon } from 'styles/assets/icons';
import DropDownArrowUpIcon from 'styles/assets/icons/DropDownArrowUpIcon';
import { ContractType } from 'types/IProducts';

import { trackDropDownChange } from './mixpanels';
import styles from './QuantityInput.module.scss';

const CurrencyDropdown = ({ inputRef }: { inputRef: RefObject<HTMLInputElement> }) => {
  const dispatch = useAppDispatch();
  const selectedProduct = useAppSelector(selectedProductSelector);
  const contractType = selectedProduct?.contract_type || 'future';
  // currency dropdown selector
  const currencyDropdownOption = useAppSelector(quantityDropdownValueSelector);
  const quantityCurrency = useAppSelector(quantityInputDropdownValue);
  const getCalculatedQuantity = useAppSelectorOnEvent(
    calculatedQuantityOnDropdownChangeSelector
  );
  const getDefaultCurrency = useAppSelectorOnEvent(defaultCurrencySelector);
  const getUpdatedPreference = useAppSelectorOnEvent(
    quantityCurrencyInPreferencesSelector
  );
  const qtyInput = useAppSelector(quantityInputValue);
  const { activeTab } = useAppSelector(appDrawerSelector);

  useEffect(() => {
    dispatch(updateDropDown(getDefaultCurrency()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProduct]);

  const handleDropdownChange = ({ value }: { value: string }) => {
    measure.start(`QuantityDropdownClick_v2_${value}`);
    dispatch(updateDropDown(value));
    trackDropDownChange(selectedProduct, value);
    // take out of current execution stack
    const updatedPreference = getUpdatedPreference();
    dispatch(updateUserPreference(updatedPreference, { disable_notification: true }));

    // do not do anything if quantity field is empty
    if (qtyInput !== null) {
      // calculate the value to be inserted in input
      const inputVal = getCalculatedQuantity(
        isSpotContract(contractType) ? qtyInput : null
      );
      // dispatch(quantityChangeEpic(inputVal));
      // dispatching causes the issue where number of contracts change on inverse futures
      inputRef?.current?.changeValue?.(inputVal);
    }
    measure.end(`QuantityDropdownClick_v2_${value}`);
  };

  if (
    (contractType &&
      (contractType === ContractType.OptionsCombos ||
        isOptions(contractType) ||
        isFutures(contractType))) ||
    contractType === ContractType.Spot
  ) {
    return (
      <Dropdown
        data-testid="quantity-currency-dropdown"
        controlClassName={cx(
          'flex-set pr-0 market-placeorder-currency-dropdown naked',
          styles.currencyDropdown
        )}
        menuClassName={cx('font-fontRegular font-size-sm2-fix', styles.dropdownMenu, {
          [styles.directionUp]: activeTab === APP_DRAWER_TAB_ID_MAPPING.CHART_ORDERBOOK,
        })}
        options={currencyDropdownOption ?? [UNIT_OF_ASSETS_QUANTITY]}
        value={quantityCurrency}
        onChange={handleDropdownChange}
        arrowClosed={<DropDownArrowUpIcon />}
        arrowOpen={<DropDownArrowDownIcon />}
      />
    );
  }

  return (
    (<div
      className={cx('font-size-sm2-fix', styles.currencyLabel)}
      data-palette="CurrencyDropdown">
      {quantityCurrency}
    </div>)
  );
};

export default CurrencyDropdown;
